/**
 * Copyright © 2025 Delicious AI, LLC
 */

import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import { Close, CloudUpload, CopyAll, Upload } from '@mui/icons-material'
import {
  Alert,
  Avatar,
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Icon,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import React, { useState } from 'react'

import CenteredBox from '@/components/CenteredBox'
import {
  GET_TENANT_DISPLAYS,
  GET_UPC_DICT,
  GetTenantDisplaysQuery,
  GetTenantDisplaysQueryVariables,
  GetUpcDictQuery,
  GetUpcDictQueryVariables,
  SET_PLANOGRAM,
  SetPlanogramMutation,
  SetPlanogramMutationVariables,
} from '@/graphql'
import useT from '@/i18n/useT'
import PlanogramFromUPCs from '@/pages/compliance/PlanogramFromUPCs'

import { GEMINI_PROMPT, JSON_SCHEMA } from '../constants'
import {
  ImportedPlanogram,
  validateAndConvertPlanograms,
} from '../helpers/import-planogram.helpers'
import { usePsaFileUpload } from '../hooks/use-psa-file-upload'
import { CreateDisplayDialog } from './CreateDisplayDialog'

interface Props {
  open: boolean
  onClose: () => void
  storeId: string
  onSuccess?: () => void
}

type ImportFileType = 'deliciousAI' | 'psa' | null

const steps = (
  t: (key: string, defaultValue?: string) => string,
  fileType: ImportFileType,
) => [
  t('importPlanogram.selectFileType', 'Select File Type'),
  fileType === 'psa'
    ? t('importPlanogram.uploadPSA', 'Upload PSA File')
    : t('importPlanogram.uploadJSON'),
  t('importPlanogram.reviewPlanograms'),
  t('importPlanogram.assignToDisplays'),
  t('importPlanogram.submit'),
]

export const ImportManyPlanogramsDialog: React.FC<Props> = ({
  open,
  onClose,
  storeId,
  onSuccess,
}) => {
  const { t } = useT()
  const [activeStep, setActiveStep] = useState(0)
  const [fileType, setFileType] = useState<ImportFileType>(null)
  const [planograms, setPlanograms] = useState<ImportedPlanogram[]>([])
  const [error, setError] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false)
  const [loadingJson, setLoadingJson] = useState(false)
  const [previewPlanogram, setPreviewPlanogram] =
    useState<ImportedPlanogram | null>(null)
  const [createDisplayDialogOpen, setCreateDisplayDialogOpen] = useState(false)
  const [createDisplayForIndex, setCreateDisplayForIndex] = useState<
    number | null
  >(null)
  const [submitProgress, setSubmitProgress] = useState(0)

  // Fetch displays for the store
  const { data: displaysData, loading: displaysLoading } = useQuery<
    GetTenantDisplaysQuery,
    GetTenantDisplaysQueryVariables
  >(GET_TENANT_DISPLAYS, {
    variables: {
      filters: {
        store: {
          id: storeId,
        },
      },
      pagination: { limit: 100, offset: 0 },
    },
    skip: !open,
  })

  const [setPlanogramMutation, { loading: submitting }] = useMutation<
    SetPlanogramMutation,
    SetPlanogramMutationVariables
  >(SET_PLANOGRAM)

  const { handlePsaFileUpload: _handlePsaFileUpload } = usePsaFileUpload({
    onSuccess: imported => {
      setPlanograms(imported)
      if (imported.length > 0) setActiveStep(2)
    },
    onError: setError,
    onLoadingChange: setLoadingJson,
  })

  const handlePsaFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    setError(null)
    _handlePsaFileUpload(event)
  }

  const displays = displaysData?.tenantDisplays.results || []

  // Convert planogram to format expected by PlanogramFromUPCs
  const convertToUPCFormat = (
    planogram: ImportedPlanogram,
  ): { upc: string }[][] => {
    const upcFormat = planogram.shelves.map(shelf => {
      const facings: { upc: string }[] = []
      shelf.products.forEach(product => {
        for (let i = 0; i < product.facingQuantity; i++) {
          facings.push({ upc: product.upc })
        }
      })
      return facings
    })
    return upcFormat
  }

  const handleOpenPreview = (planogram: ImportedPlanogram) => {
    setPreviewPlanogram(planogram)
    setPreviewDialogOpen(true)
  }

  const handleClosePreview = () => {
    setPreviewDialogOpen(false)
    setPreviewPlanogram(null)
  }

  const [getUpcDict] = useLazyQuery<GetUpcDictQuery, GetUpcDictQueryVariables>(
    GET_UPC_DICT,
    {
      fetchPolicy: 'cache-first',
    },
  )

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setLoadingJson(true)
    const file = event.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = async e => {
      const json = JSON.parse(e.target?.result as string)

      const result = await validateAndConvertPlanograms(json, getUpcDict, t)

      if (result.error) {
        setError(result.error)
      }
      setPlanograms(result.planograms)

      if (result.planograms.length > 0) {
        setActiveStep(2)
      }

      setLoadingJson(false)
    }
    reader.readAsText(file)
  }

  const handleNext = () => {
    if (activeStep === 3) {
      // Validate that all planograms have displays assigned
      const unassigned = planograms.filter(p => !p.displayId)
      if (unassigned.length > 0) {
        setError(
          `${t('importPlanogram.pleaseAssignDisplays')} (${unassigned.length} ${t('importPlanogram.remaining')})`,
        )
        return
      }
      setError(null)
    }
    setActiveStep(prev => prev + 1)
  }

  const handleBack = () => {
    setActiveStep(prev => prev - 1)
    setError(null)
  }

  const handleAssignDisplay = (planogramIndex: number, displayId: string) => {
    setPlanograms(prev =>
      prev.map((p, i) => (i === planogramIndex ? { ...p, displayId } : p)),
    )
  }

  const handleSubmit = async () => {
    setUploading(true)
    setError(null)
    setSubmitProgress(0)

    try {
      // Submit each planogram (door)
      for (const planogram of planograms) {
        if (!planogram.displayId) {
          throw new Error(
            t('importPlanogram.doorHasNoDisplay', { number: planogram.door }),
          )
        }

        // Convert door/shelf/product structure to shapedUpcs format
        // Create a single segment (segmentIndex: 0) with all shelves
        const shelfFacings = planogram.shelves.map((shelf, shelfIndex) => {
          const facings: Array<{
            columnIndex: number
            upc: string
            quantity: number
          }> = []
          let columnIndex = 0

          // For each product, create facings based on facingQuantity
          shelf.products.forEach(product => {
            for (let i = 0; i < product.facingQuantity; i++) {
              facings.push({
                columnIndex,
                upc: product.upc,
                quantity: 1,
              })
              columnIndex++
            }
          })

          return {
            displayShelfIndex: shelfIndex,
            segmentShelfIndex: shelfIndex,
            planogramfacings: {
              create: facings,
            },
          }
        })

        await setPlanogramMutation({
          variables: {
            data: {
              display: {
                assign: planogram.displayId,
              },
              segments: {
                create: [
                  {
                    segmentIndex: 0,
                    shelves: {
                      create: shelfFacings,
                    },
                  },
                ],
              },
            },
          },
        })
        setSubmitProgress(prev => prev + 1)
      }

      // Success - close dialog and notify parent
      onSuccess?.()
      handleClose()
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t('importPlanogram.failedToSubmitPlanograms'),
      )
    } finally {
      setUploading(false)
    }
  }

  const handleSetFileType = (type: ImportFileType) => {
    setFileType(type)
    setPlanograms([])
  }

  const handleClose = () => {
    setActiveStep(0)
    setFileType(null)
    setPlanograms([])
    setError(null)
    setUploading(false)
    setSubmitProgress(0)
    onClose()
  }

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ py: 4 }}>
            <Typography variant="h6" gutterBottom>
              {t('importPlanogram.selectImportTypeTitle', 'Select Import Type')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t(
                'importPlanogram.selectImportTypeDescription',
                'Select the format that matches your files',
              )}
            </Typography>
            <Stack direction="row" spacing={2}>
              <ButtonBase
                onClick={() => handleSetFileType('deliciousAI')}
                focusRipple
                sx={{
                  flex: 1,
                  alignItems: 'stretch',
                  textAlign: 'left',
                  borderRadius: 1,
                }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    width: '100%',
                    p: 3,
                    borderColor:
                      fileType === 'deliciousAI' ? 'primary.main' : 'divider',
                    borderWidth: fileType === 'deliciousAI' ? 2 : 1,
                    '&:hover': { borderColor: 'primary.main' },
                    '.Mui-focusVisible &': { borderColor: 'primary.main' },
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    {t(
                      'importPlanogram.deliciousAIFiles',
                      'Delicious AI Schema & PDFs',
                    )}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    {t(
                      'importPlanogram.deliciousAIFilesDescription',
                      'If you have PDFs, select this option and follow the instructions to extract the planogram schema using Delicious AI. We also accept JSON files that match our schema.',
                    )}
                  </Typography>
                </Paper>
              </ButtonBase>
              <ButtonBase
                onClick={() => handleSetFileType('psa')}
                focusRipple
                sx={{
                  flex: 1,
                  alignItems: 'stretch',
                  textAlign: 'left',
                  borderRadius: 1,
                }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    width: '100%',
                    p: 3,
                    borderColor:
                      fileType === 'psa' ? 'primary.main' : 'divider',
                    borderWidth: fileType === 'psa' ? 2 : 1,
                    '&:hover': { borderColor: 'primary.main' },
                    '.Mui-focusVisible &': { borderColor: 'primary.main' },
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    {t('importPlanogram.psaFiles', 'PSA Files')}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    {t(
                      'importPlanogram.psaFilesDescription',
                      'File format from Blue Yonder planogram software.',
                    )}
                  </Typography>
                </Paper>
              </ButtonBase>
            </Stack>
          </Box>
        )

      case 1:
        return (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            {loadingJson ? (
              <CenteredBox>
                <CircularProgress />
              </CenteredBox>
            ) : fileType === 'psa' ? (
              <Stack alignItems="center" spacing={2}>
                <CloudUpload
                  sx={{ fontSize: 64, color: 'primary.main', mb: 2 }}
                />
                <Typography variant="body2" color="text.secondary">
                  {t(
                    'importPlanogram.uploadPSADescription',
                    'Upload .psa file',
                  )}
                </Typography>
                <Button
                  variant="contained"
                  component="label"
                  startIcon={<Upload />}
                >
                  {t('importPlanogram.chooseFile')}
                  <input
                    type="file"
                    hidden
                    accept=".psa"
                    onChange={handlePsaFileUpload}
                  />
                </Button>
              </Stack>
            ) : (
              <>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ justifyContent: 'center', mt: 8 }}
                >
                  <Typography variant="h6" gutterBottom component="span">
                    {t('importPlanogram.clickToCopyLLMPrompt')}
                  </Typography>
                  <Icon
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigator.clipboard.writeText(GEMINI_PROMPT)}
                  >
                    <CopyAll />
                  </Icon>
                </Stack>
                <Stack sx={{ mt: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    {t('importPlanogram.jsonSchema')}
                  </Typography>
                  <Box
                    sx={{
                      width: '100%',
                      justifyContent: 'center',
                      display: 'flex',
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2, textAlign: 'left', width: 'fit-content' }}
                    >
                      <pre>{JSON_SCHEMA}</pre>
                    </Typography>
                  </Box>
                </Stack>
                <Stack alignItems="center">
                  <CloudUpload
                    sx={{ fontSize: 64, color: 'primary.main', mb: 2 }}
                  />
                  <Button
                    variant="contained"
                    component="label"
                    startIcon={<Upload />}
                  >
                    {t('importPlanogram.chooseFile')}
                    <input
                      type="file"
                      hidden
                      accept=".json"
                      onChange={handleFileUpload}
                    />
                  </Button>
                </Stack>
              </>
            )}
          </Box>
        )

      case 2:
        return (
          <Box sx={{ py: 2 }}>
            <Typography variant="h6" gutterBottom>
              {t('importPlanogram.reviewImportedPlanograms')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('importPlanogram.doorsImported', { count: planograms.length })}
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('importPlanogram.door')}</TableCell>
                    <TableCell align="right">
                      {t('importPlanogram.shelves')}
                    </TableCell>
                    <TableCell align="right">
                      {t('importPlanogram.totalFacings')}
                    </TableCell>
                    <TableCell align="right">
                      {t('importPlanogram.uniqueProducts')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {planograms.map((planogram, index) => {
                    const totalFacings = planogram.shelves.reduce(
                      (sum, shelf) =>
                        sum +
                        shelf.products.reduce(
                          (prodSum, prod) => prodSum + prod.facingQuantity,
                          0,
                        ),
                      0,
                    )
                    const uniqueProducts = new Set(
                      planogram.shelves.flatMap(shelf =>
                        shelf.products.map(p => p.upc),
                      ),
                    ).size
                    return (
                      <TableRow key={index}>
                        <TableCell
                          onClick={() => handleOpenPreview(planogram)}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                        >
                          {t('importPlanogram.doorNumber', {
                            number: planogram.door,
                          })}
                        </TableCell>
                        <TableCell align="right">
                          {planogram.shelves.length}
                        </TableCell>
                        <TableCell align="right">{totalFacings}</TableCell>
                        <TableCell align="right">{uniqueProducts}</TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )

      case 3:
        return (
          <Box sx={{ py: 2 }}>
            <Typography variant="h6" gutterBottom>
              {t('importPlanogram.assignPlanogramsToDisplays')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('importPlanogram.selectDisplayForEachPlanogram')}
            </Typography>
            {displaysLoading ? (
              <Typography>{t('importPlanogram.loadingDisplays')}</Typography>
            ) : (
              <Stack spacing={2}>
                {planograms.map((planogram, index) => {
                  const totalFacings = planogram.shelves.reduce(
                    (sum, shelf) =>
                      sum +
                      shelf.products.reduce(
                        (prodSum, prod) => prodSum + prod.facingQuantity,
                        0,
                      ),
                    0,
                  )
                  return (
                    <Paper key={index} variant="outlined" sx={{ p: 2 }}>
                      <Stack
                        direction="row"
                        spacing={2}
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle1">
                            {t('importPlanogram.doorNumber', {
                              number: planogram.door,
                            })}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('importPlanogram.shelvesAndFacings', {
                              shelves: planogram.shelves.length,
                              facings: totalFacings,
                            })}
                          </Typography>
                        </Box>
                        <FormControl sx={{ minWidth: 250 }} size="small">
                          <InputLabel>
                            {t('importPlanogram.display')}
                          </InputLabel>
                          <Select
                            value={planogram.displayId || ''}
                            label={t('importPlanogram.display')}
                            onChange={e => {
                              if (e.target.value === '__create_new__') {
                                setCreateDisplayForIndex(index)
                                setCreateDisplayDialogOpen(true)
                              } else {
                                handleAssignDisplay(index, e.target.value)
                              }
                            }}
                          >
                            {displays.map(display => (
                              <MenuItem
                                key={display.id}
                                value={display.id}
                                sx={{ height: 200 }}
                              >
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  spacing={1.5}
                                  sx={{ width: '100%', height: '100%' }}
                                >
                                  <Avatar
                                    src={display.image?.url || undefined}
                                    alt={display.name}
                                    sx={{
                                      width: 160,
                                      height: 160,
                                      border: '1px solid',
                                      borderColor: 'divider',
                                    }}
                                    variant="rounded"
                                  >
                                    {!display.image?.url &&
                                      display.name.charAt(0)}
                                  </Avatar>
                                  <Box sx={{ flex: 1 }}>
                                    <Typography variant="body2">
                                      {display.name}
                                    </Typography>
                                    {display.planogram && (
                                      <Chip
                                        label={t(
                                          'importPlanogram.hasPlanogram',
                                        )}
                                        size="small"
                                        color="warning"
                                        sx={{
                                          height: 16,
                                          fontSize: '0.625rem',
                                          mt: 0.5,
                                        }}
                                      />
                                    )}
                                  </Box>
                                </Stack>
                              </MenuItem>
                            ))}
                            <MenuItem value="__create_new__">
                              <Typography variant="body2" color="primary">
                                {t('display.createNew', 'Create New Display')}
                              </Typography>
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Stack>
                    </Paper>
                  )
                })}
              </Stack>
            )}
          </Box>
        )

      case 4:
        return (
          <Box sx={{ py: 2 }}>
            <Typography variant="h6" gutterBottom>
              {t('importPlanogram.confirmImport')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('importPlanogram.reviewAssignmentsBeforeSubmitting')}
            </Typography>
            {uploading && (
              <Box sx={{ mb: 2 }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 0.5 }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {t('importPlanogram.submitting')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {submitProgress} / {planograms.length}
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={(submitProgress / planograms.length) * 100}
                />
              </Box>
            )}
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('importPlanogram.door')}</TableCell>
                    <TableCell>{t('importPlanogram.display')}</TableCell>
                    <TableCell align="right">
                      {t('importPlanogram.shelves')}
                    </TableCell>
                    <TableCell align="right">
                      {t('importPlanogram.facings')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {planograms.map((planogram, index) => {
                    const display = displays.find(
                      d => d.id === planogram.displayId,
                    )
                    const totalFacings = planogram.shelves.reduce(
                      (sum, shelf) =>
                        sum +
                        shelf.products.reduce(
                          (prodSum, prod) => prodSum + prod.facingQuantity,
                          0,
                        ),
                      0,
                    )
                    return (
                      <TableRow key={index}>
                        <TableCell
                          onClick={() => handleOpenPreview(planogram)}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                        >
                          {t('importPlanogram.doorNumber', {
                            number: planogram.door,
                          })}
                        </TableCell>
                        <TableCell>
                          {display?.name || t('importPlanogram.unknown')}
                          {display?.planogram && (
                            <Chip
                              label={t('importPlanogram.willReplaceExisting')}
                              size="small"
                              sx={{ ml: 1 }}
                              color="warning"
                            />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {planogram.shelves.length}
                        </TableCell>
                        <TableCell align="right">{totalFacings}</TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )

      default:
        return null
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      slotProps={{ paper: { sx: { minHeight: 500 } } }}
    >
      <DialogTitle>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6">{t('importPlanogram.title')}</Typography>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>

      <Stepper activeStep={activeStep} sx={{ px: 3, pt: 2 }}>
        {steps(t, fileType).map(label => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {renderStepContent()}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={uploading || submitting}>
          {t('importPlanogram.cancel')}
        </Button>
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={uploading || submitting}>
            {t('importPlanogram.back')}
          </Button>
        )}
        {activeStep < steps(t, fileType).length - 1 ? (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={
              (activeStep === 0 && fileType === null) ||
              (activeStep === 1 && planograms.length === 0) ||
              uploading ||
              submitting
            }
          >
            {t('importPlanogram.next')}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={uploading || submitting}
          >
            {submitting
              ? t('importPlanogram.submitting')
              : t('importPlanogram.submitAll')}
          </Button>
        )}
      </DialogActions>

      <CreateDisplayDialog
        open={createDisplayDialogOpen}
        onClose={() => setCreateDisplayDialogOpen(false)}
        storeId={storeId}
        onCreated={displayId => {
          if (createDisplayForIndex !== null && displayId) {
            handleAssignDisplay(createDisplayForIndex, displayId)
          }
          setCreateDisplayForIndex(null)
        }}
      />

      <Dialog
        open={previewDialogOpen}
        onClose={handleClosePreview}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">
              {previewPlanogram &&
                t('importPlanogram.doorPreview', {
                  number: previewPlanogram.door,
                })}
            </Typography>
            <IconButton onClick={handleClosePreview} size="small">
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ width: 400 }}>
          {previewPlanogram && (
            <Box sx={{ py: 2 }}>
              <PlanogramFromUPCs plan={convertToUPCFormat(previewPlanogram)} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreview}>
            {t('importPlanogram.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  )
}

export default ImportManyPlanogramsDialog
